/**
 * DashboardLayout — shadcn/ui Dashboard-2 reference architecture.
 *
 * Hierarchy (matches official ShadcnStore Dashboard-2 pattern):
 *
 *   SidebarProvider
 *   ├── Sidebar  (collapsible="offcanvas", side="left")
 *   │   ├── SidebarHeader  — logo + brand
 *   │   ├── SidebarContent — scrollable nav groups
 *   │   │   ├── SidebarGroup  (primary items + CTA)
 *   │   │   ├── SidebarGroup  (Platform — labeled)
 *   │   │   └── SidebarGroup  (secondary — mt-auto)
 *   │   ├── SidebarFooter  — NavUser
 *   │   └── SidebarRail
 *   └── SidebarInset
 *       ├── <header>  h-14, sticky, SidebarTrigger + Search + Notifications + Avatar
 *       └── <main>    flex-1, overflow-y-auto  ← pages own their spacing
 *
 * All business logic preserved: RBAC, conversation count poll,
 * logout, CommandBar, keyboard shortcuts.
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLayoutConfig, type SidebarVariant, type SidebarCollapsible, type SidebarSide } from '../../hooks/use-layout-config';
import { CustomizerPanel } from '../customizer/customizer-panel';
import {
  LayoutDashboard,
  MessageSquare,
  Users,
  Inbox,
  Puzzle,
  BarChart3,
  RefreshCcw,
  Tag,
  Users2,
  User,
  Shield,
  MessageCircle,
  Megaphone,
  Phone,
  Mail,
  ClipboardList,
  UserCog,
  Network,
  Clock,
  BookOpen,
  ThumbsUp,
  Settings,
  LogOut,
  Search,
  ChevronDown,
  Sparkles,
  Ticket,
  Truck,
} from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { logout } from '../../store/slices/authSlice';
import { useKeyboardShortcuts } from '../../contexts/KeyboardShortcutsContext';
import CommandBar from '../CommandBar/CommandBar';
import ShortcutsHelpModal from '../KeyboardShortcuts/ShortcutsHelpModal';
import NotificationsDropdown from '../Notifications/NotificationsDropdown';
import { conversationsAPI } from '../../services/api';
import { cn } from '../../lib/utils';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
} from '../ui/sidebar';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { Toaster } from '../ui/toaster';
import { BrandLogo } from '../BrandLogo';
import { CallingProvider, useCallingContext } from '../Calling/CallingContext';
import { IncomingCallModal } from '../Calling/IncomingCallModal';
import { UnifiedCallHub } from '../Calling/UnifiedCallHub';
import { CallState } from '../../utils/webrtc-config';
import { useVapi } from '../../contexts/VapiContext';
import SmartToyIcon from '@mui/icons-material/SmartToy';
// 2026-08-18: Digo Phase 1 (Docs/DIGO_VOICE_ADMIN_ASSISTANT_PLAN.md §7/§8) —
// global admin assistant launcher, mounted once below alongside the other
// floating overlays so it's present on every dashboard page, not one route.
// 2026-09-09: disabled per request — commented out, not removed.
// import DigoLauncher from '../Digo/DigoLauncher';

// ─── Route → page title ────────────────────────────────────────────────────────
const PAGE_TITLES: Record<string, string> = {
  '/dashboard/conversations':      'Conversations',
  '/dashboard/contacts':           'Contacts',
  '/dashboard/inboxes':            'Inboxes',
  '/dashboard/integrations':       'Integrations',
  '/dashboard/ai-studio':          'ConverseFlow',
  '/dashboard/reports':            'Reports',
  '/dashboard/tickets':            'Issued Tickets',
  '/dashboard/packages':           'Service Packages',
  '/dashboard/automation':         'Automation',
  '/dashboard/labels':             'Labels',
  '/dashboard/teams':              'Teams',
  '/dashboard/agents':             'Agents',
  '/dashboard/roles':              'Roles & Permissions',
  '/dashboard/canned-responses':   'Canned Responses',
  '/dashboard/campaigns':          'Campaigns',
  '/dashboard/whatsapp-templates': 'WhatsApp Templates',
  '/dashboard/email-templates':    'Email Templates',
  '/dashboard/pre-chat-forms':     'Pre-Chat Forms',
  '/dashboard/agent-capacity':     'Agent Capacity',
  '/dashboard/slack-integration':  'Slack Integration',
  '/dashboard/business-hours':     'Business Hours',
  '/dashboard/knowledge-base':     'Knowledge Base',
  '/dashboard/csat-analytics':     'CSAT Analytics',
  '/dashboard/settings':           'Settings',
  '/dashboard':                    'Dashboard',
};

const getPageTitle = (pathname: string): string =>
  Object.entries(PAGE_TITLES).find(([p]) => pathname.startsWith(p))?.[1] ?? 'Dashboard';

// ─── Types ─────────────────────────────────────────────────────────────────────
type NavGroup = 'main' | 'channels' | 'manage' | 'configure' | 'admin';

interface MenuItem {
  text: string;
  icon: React.ComponentType<{ className?: string }>;
  path: string;
  badge?: number;
  requiredPermissions?: string[];
  group: NavGroup;
}

// ─── AppSidebar ────────────────────────────────────────────────────────────────
interface AppSidebarProps {
  menuItems: MenuItem[];
  isActive: (path: string) => boolean;
  onNavigate: (path: string) => void;
  userName: string;
  userInitial: string;
  userEmail: string;
  avatarUrl?: string;
  sidebarVariant: SidebarVariant;
  sidebarCollapsible: SidebarCollapsible;
  sidebarSide: SidebarSide;
}

const GROUP_CONFIG: Array<{ key: NavGroup; label: string }> = [
  { key: 'main',      label: 'MAIN'      },
  { key: 'channels',  label: 'CHANNELS'  },
  { key: 'manage',    label: 'MANAGE'    },
  { key: 'configure', label: 'CONFIGURE' },
  { key: 'admin',     label: 'ADMIN'     },
];

/**
 * Renders the incoming-call modal and the persistent video call widget
 * at the layout level so they survive navigation between dashboard routes.
 * Must be a child of CallingProvider (already the case in DashboardLayout).
 */
const GlobalCallingWidgets: React.FC = () => {
  const calling = useCallingContext();
  if (!calling) return null;

  const { callState, incomingCallData, acceptCall, declineCall } = calling;
  const isRinging = callState === CallState.RINGING && incomingCallData !== null;

  return (
    <>
      <IncomingCallModal
        isOpen={isRinging}
        onClose={() => {/* closes automatically when callState leaves RINGING */}}
        onAccept={(sid) => acceptCall(sid, incomingCallData?.call_type)}
        onDecline={(sid) => declineCall(sid)}
        sessionId={incomingCallData?.session_id}
        callerInfo={incomingCallData ?? undefined}
      />
      {/* Unified calling hub — AI Agent + Direct Call tabs in one draggable widget.
          Both CallingContext and VapiContext are in scope at this level. */}
      <UnifiedCallHub />
    </>
  );
};

interface DashboardLayoutProps {
  children: React.ReactNode;
}

const VapiToolbarButton: React.FC = () => {
  const { callStatus, endCall, hubOpen, openHub, closeHub } = useVapi();
  const isIdle    = callStatus === 'idle';
  const isLoading = callStatus === 'loading';
  const isActive  = !isIdle && !isLoading;

  const handleClick = () => {
    if (isActive) {
      endCall();
      closeHub();
    } else {
      openHub();   // always open the hub; user starts the call from inside it
    }
  };

  const title = hubOpen && !isActive ? 'AI Agent Hub' : isActive ? 'End AI Assistant' : 'Open AI Agent';
  return (
    <button
      title={title}
      onClick={handleClick}
      disabled={isLoading}
      className={cn(
        'flex h-8 w-8 items-center justify-center rounded-lg mr-1 transition-colors focus:outline-none focus:ring-2 focus:ring-ring',
        (hubOpen || isActive)
          ? 'text-[#1f93ff] bg-[#1f93ff]/10 hover:bg-[#1f93ff]/20'
          : 'text-muted-foreground hover:bg-accent',
      )}
    >
      <SmartToyIcon className="h-4 w-4" />
    </button>
  );
};

const AppSidebar: React.FC<AppSidebarProps> = ({
  menuItems,
  isActive,
  onNavigate,
  userName,
  userInitial,
  userEmail,
  avatarUrl,
  sidebarVariant,
  sidebarCollapsible,
  sidebarSide,
}) => {
  const { state, setOpen, isMobile, setOpenMobile } = useSidebar();

  /** Paths that auto-close the sidebar on click (content-heavy full-screen pages) */
  const AUTO_CLOSE_PATHS = ['/dashboard/reports', '/dashboard/settings'];

  /** Close sidebar after navigation: collapse to icon rail on desktop, hide on mobile */
  const closeSidebar = () => {
    if (isMobile) setOpenMobile(false);
    else setOpen(false);
  };

  const [openGroups, setOpenGroups] = useState<Set<NavGroup>>(
    new Set(['main', 'channels', 'manage', 'configure', 'admin'] as NavGroup[])
  );

  const toggleGroup = (group: NavGroup) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      return next;
    });
  };

  /** Renders a list of menu items into SidebarMenuButtons */
  const renderItems = (items: MenuItem[]) =>
    items.map((item) => {
      const Icon = item.icon;
      const active = isActive(item.path);
      return (
        <SidebarMenuItem key={item.path}>
          <SidebarMenuButton
            isActive={active}
            onClick={() => { onNavigate(item.path); if (AUTO_CLOSE_PATHS.some(p => item.path.startsWith(p))) closeSidebar(); }}
            tooltip={item.text}
          >
            {/* Icon: full opacity when active, muted when idle */}
            <Icon className={cn('h-4 w-4 shrink-0 transition-colors', active ? 'opacity-100' : 'opacity-50')} />
            {/* Text: semibold when active, regular when idle */}
            <span className={cn('transition-[font-weight]', active ? 'font-semibold' : 'font-normal')}>
              {item.text}
            </span>
            {item.badge ? (
              <Badge
                variant="secondary"
                className="ml-auto h-5 min-w-5 justify-center px-1.5 text-xs tabular-nums"
              >
                {item.badge > 99 ? '99+' : item.badge}
              </Badge>
            ) : null}
          </SidebarMenuButton>
        </SidebarMenuItem>
      );
    });

  return (
    <Sidebar collapsible={sidebarCollapsible} variant={sidebarVariant} side={sidebarSide}>

      {/* ── SidebarHeader ─────────────────────────────────────────────── */}
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              size="lg"
              onClick={() => onNavigate('/dashboard')}
              className="data-[slot=sidebar-menu-button]:!p-1.5 group-data-[collapsible=icon]:!p-0 group-data-[collapsible=icon]:justify-center"
            >
              <BrandLogo
                variant="mark"
                className="h-7 w-7 shrink-0 group-data-[collapsible=icon]:h-6 group-data-[collapsible=icon]:w-6"
              />
              <div className="grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
                <span className="truncate font-semibold">Converse</span>
                <span className="truncate text-xs text-muted-foreground">Support Platform</span>
              </div>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      {/* ── SidebarContent ────────────────────────────────────────────── */}
      <SidebarContent>
        {GROUP_CONFIG.map(({ key, label }, groupIndex) => {
          const items = menuItems.filter((i) => i.group === key);
          if (items.length === 0) return null;
          const isOpen = openGroups.has(key);
          return (
            <SidebarGroup
              key={key}
              className={cn('py-0', groupIndex > 0 && 'mt-1 border-t border-sidebar-border/70 pt-2')}
            >
              <button
                type="button"
                onClick={() => toggleGroup(key)}
                className="group-data-[collapsible=icon]:hidden flex w-full items-center px-2 py-2 text-left focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-sidebar-ring rounded-sm"
              >
                <span className="flex-1 text-[11px] font-bold uppercase tracking-[0.08em] text-sidebar-foreground/80 select-none">
                  {label}
                </span>
                <ChevronDown
                  className={cn(
                    'h-3 w-3 shrink-0 text-sidebar-foreground/60 transition-transform duration-200',
                    isOpen && 'rotate-180',
                  )}
                />
              </button>
              <div
                className={cn(
                  'grid transition-[grid-template-rows] duration-300 ease-in-out',
                  (isOpen || state === 'collapsed') ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
                )}
              >
                <div className="overflow-hidden">
                  <SidebarMenu className="mt-0.5">{renderItems(items)}</SidebarMenu>
                </div>
              </div>
            </SidebarGroup>
          );
        })}
      </SidebarContent>

      {/* ── SidebarFooter: NavUser ─────────────────────────────────────── */}
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              size="lg"
              className="data-[slot=sidebar-menu-button]:!p-1.5 group-data-[collapsible=icon]:!p-0 group-data-[collapsible=icon]:justify-center"
              tooltip={userName}
            >
              <Avatar className="h-8 w-8 rounded-lg shrink-0">
                <AvatarImage src={avatarUrl} alt={userName} />
                <AvatarFallback className="rounded-lg bg-primary text-[11px] font-semibold text-primary-foreground">
                  {userInitial}
                </AvatarFallback>
              </Avatar>
              <div className="grid min-w-0 flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
                <span className="truncate font-semibold text-sidebar-foreground">{userName}</span>
                {userEmail && (
                  <span className="truncate text-xs text-muted-foreground">{userEmail}</span>
                )}
              </div>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  );
};

// ─── DashboardTopbar ───────────────────────────────────────────────────────────
// Separated so it can call useSidebar() (must be inside SidebarProvider)
interface DashboardTopbarProps {
  userName: string;
  userInitial: string;
  avatarUrl?: string;
  pageTitle: string;
  onSearch: () => void;
  onNavigate: (path: string) => void;
  onLogout: () => void;
}

const DashboardTopbar: React.FC<DashboardTopbarProps> = ({
  userName,
  userInitial,
  avatarUrl,
  pageTitle,
  onSearch,
  onNavigate,
  onLogout,
}) => {
  const { state } = useSidebar();
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(e.target as Node))
        setProfileOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <header className="flex h-14 shrink-0 items-center gap-3 border-b border-border bg-background/95 px-3 backdrop-blur-sm sticky top-0 z-30 transition-[width] ease-linear md:px-4">

      {/* Left: trigger */}
      <div className="flex shrink-0 items-center gap-2">
        <SidebarTrigger className="-ml-1" />
        <Separator orientation="vertical" className="h-4 hidden md:block" />
      </div>

      {/* Center: search expands into available space (was collapsed at max-w-[200px]) */}
      <button
        onClick={onSearch}
        className="flex h-9 min-w-0 flex-1 items-center gap-2 rounded-md border border-input bg-background px-3 text-sm text-muted-foreground shadow-none transition-colors hover:bg-accent hover:text-accent-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 md:max-w-xl"
        aria-label="Search"
      >
        <Search className="h-4 w-4 shrink-0" />
        <span className="flex-1 truncate text-left text-sm">Search...</span>
        <kbd className="pointer-events-none hidden select-none items-center gap-0.5 rounded border border-border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground sm:flex">
          <span className="text-[11px]">⌘</span>K
        </kbd>
      </button>

      {/* Right: page title + notifications + avatar */}
      <div className="ml-auto flex shrink-0 items-center gap-2">
        {/* Page title — shown when sidebar is collapsed */}
        <span
          className={cn(
            'hidden text-sm font-semibold text-foreground transition-opacity duration-200 lg:block',
            state === 'collapsed' ? 'opacity-100' : 'opacity-0 pointer-events-none w-0 overflow-hidden',
          )}
        >
          {pageTitle}
        </span>

        <VapiToolbarButton />

        <NotificationsDropdown />

        {/* Avatar + profile dropdown */}
        <div ref={profileRef} className="relative">
          <button
            onClick={() => setProfileOpen((v) => !v)}
            className="flex h-8 w-8 items-center justify-center rounded-lg transition-colors hover:bg-accent focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1"
            aria-label="User menu"
            aria-expanded={profileOpen}
          >
            <Avatar className="h-7 w-7 rounded-lg">
              <AvatarImage src={avatarUrl} alt={userName} />
              <AvatarFallback className="rounded-lg bg-primary text-[10px] font-semibold text-primary-foreground">
                {userInitial}
              </AvatarFallback>
            </Avatar>
          </button>

          {profileOpen && (
            <div className="absolute right-0 top-full z-50 mt-2 w-52 overflow-hidden rounded-lg border border-border bg-popover shadow-md">
              <div className="flex items-center gap-2.5 p-3">
                <Avatar className="h-8 w-8 rounded-lg">
                  <AvatarImage src={avatarUrl} alt={userName} />
                  <AvatarFallback className="rounded-lg bg-primary text-xs text-primary-foreground">
                    {userInitial}
                  </AvatarFallback>
                </Avatar>
                <div className="grid min-w-0 flex-1 text-sm leading-tight">
                  <span className="truncate font-semibold text-foreground">{userName}</span>
                </div>
              </div>
              <Separator />
              <div className="p-1">
                <button
                  onClick={() => { onNavigate('/dashboard/settings'); setProfileOpen(false); }}
                  className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors hover:bg-accent hover:text-accent-foreground"
                >
                  <User className="h-4 w-4 text-muted-foreground" />
                  Profile
                </button>
              </div>
              <Separator />
              <div className="p-1">
                <button
                  onClick={onLogout}
                  className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-red-600 transition-colors hover:bg-red-500/10 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-500/15 dark:hover:text-red-300"
                >
                  <LogOut className="h-4 w-4" />
                  Log out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

// ─── DashboardLayout ───────────────────────────────────────────────────────────
const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const [conversationCount, setConversationCount] = useState<number>(0);
  const [layoutConfig, setLayoutConfig] = useLayoutConfig();

  const navigate  = useNavigate();
  const location  = useLocation();
  const dispatch  = useAppDispatch();
  const { user }  = useAppSelector((state) => state.auth);
  const { isCommandBarOpen, isHelpModalOpen, openCommandBar, closeCommandBar, closeHelpModal } =
    useKeyboardShortcuts();

  // Poll open conversation count
  useEffect(() => {
    const fetch = async () => {
      try {
        const r = await conversationsAPI.getConversations({ status: 'open' as any });
        setConversationCount(r.total || r.conversations?.length || 0);
      } catch { /* silent */ }
    };
    fetch();
    const id = setInterval(fetch, 30_000);
    return () => clearInterval(id);
  }, []);

  const handleLogout = () => { dispatch(logout()); };

  // ── Navigation items ─────────────────────────────────────────────────────────
  const allMenuItems: MenuItem[] = [
    // ── Main — core daily-use items
    { group: 'main',      text: 'Dashboard',           icon: LayoutDashboard, path: '/dashboard' },
    { group: 'main',      text: 'Conversations',        icon: MessageSquare,   path: '/dashboard/conversations',     badge: conversationCount, requiredPermissions: ['conversations:view_all','conversations:view_team','conversations:view_assigned'] },
    { group: 'main',      text: 'Contacts',             icon: Users,           path: '/dashboard/contacts',          requiredPermissions: ['contacts:view'] },
    { group: 'main',      text: 'Reports',              icon: BarChart3,       path: '/dashboard/reports',           requiredPermissions: ['reports:view'] },
    // 2026-08-08 (§6.2): previously only reachable via a toggle buried inside
    // Conversations, no route/URL/sidebar entry at all. No requiredPermissions
    // — access is account-feature-flagged (TicketsPage checks
    // complaintTicketsAPI.isEnabled()), not permission-gated, matching how the
    // backend endpoints themselves only require an active user, not a
    // specific permission.
    { group: 'main',      text: 'Issued Tickets',       icon: Ticket,          path: '/dashboard/tickets' },
    // Same reasoning as Issued Tickets above: feature-gated (ticketing must
    // be enabled — see ServiceDeliveryPage.tsx's own isEnabled check), not
    // RBAC-gated, since deliveries hang off the same ComplaintTicket rows.
    // Previously only reachable as an admin-only Settings tab (index 11)
    // nobody could find.
    { group: 'main',      text: 'Service Packages',     icon: Truck,           path: '/dashboard/packages' },
    // ── Channels — inbox & outreach management
    { group: 'channels',  text: 'Inboxes',              icon: Inbox,           path: '/dashboard/inboxes',           requiredPermissions: ['inboxes:view'] },
    { group: 'channels',  text: 'Campaigns',            icon: Megaphone,       path: '/dashboard/campaigns',         requiredPermissions: ['campaigns:view'] },
    { group: 'channels',  text: 'WhatsApp Templates',   icon: Phone,           path: '/dashboard/whatsapp-templates',requiredPermissions: ['campaigns:view'] },
    { group: 'channels',  text: 'Email Templates',      icon: Mail,            path: '/dashboard/email-templates',   requiredPermissions: ['campaigns:view'] },
    { group: 'channels',  text: 'Pre-Chat Forms',       icon: ClipboardList,   path: '/dashboard/pre-chat-forms',    requiredPermissions: ['pre_chat_forms:view'] },
    // ── Manage — automation, content & knowledge
    { group: 'manage',    text: 'Automation',           icon: RefreshCcw,      path: '/dashboard/automation',        requiredPermissions: ['automation:view'] },
    { group: 'manage',    text: 'Canned Responses',     icon: MessageCircle,   path: '/dashboard/canned-responses',  requiredPermissions: ['canned_responses:view'] },
    { group: 'manage',    text: 'Labels',               icon: Tag,             path: '/dashboard/labels',            requiredPermissions: ['labels:view'] },
    { group: 'manage',    text: 'Knowledge Base',       icon: BookOpen,        path: '/dashboard/knowledge-base',    requiredPermissions: ['knowledge_base:view'] },
    { group: 'manage',    text: 'ConverseFlow',         icon: Sparkles,        path: '/dashboard/ai-studio' },
    { group: 'manage',    text: 'Integrations',         icon: Puzzle,          path: '/dashboard/integrations',      requiredPermissions: ['integrations:view'] },
    // ── Configure — schedule & capacity settings
    { group: 'configure', text: 'Business Hours',       icon: Clock,           path: '/dashboard/business-hours',    requiredPermissions: ['business_hours:view'] },
    { group: 'configure', text: 'Agent Capacity',       icon: UserCog,         path: '/dashboard/agent-capacity' },
    { group: 'configure', text: 'CSAT Analytics',       icon: ThumbsUp,        path: '/dashboard/csat-analytics' },
    // ── Admin — team, roles & account
    { group: 'admin',     text: 'Agents',               icon: User,            path: '/dashboard/agents',            requiredPermissions: ['agents:view'] },
    { group: 'admin',     text: 'Teams',                icon: Users2,          path: '/dashboard/teams',             requiredPermissions: ['teams:view'] },
    { group: 'admin',     text: 'Roles & Permissions',  icon: Shield,          path: '/dashboard/roles',             requiredPermissions: ['roles:view'] },
    { group: 'admin',     text: 'Slack Integration',    icon: Network,         path: '/dashboard/slack-integration', requiredPermissions: ['slack_integration:view'] },
    { group: 'admin',     text: 'Settings',             icon: Settings,        path: '/dashboard/settings',          requiredPermissions: ['settings:view'] },
  ];

  // RBAC filter — logic unchanged
  const menuItems = useMemo<MenuItem[]>(() => {
    if (user?.role?.toUpperCase() === 'SUPER_ADMIN') return allMenuItems;
    return allMenuItems.filter((item) => {
      if (!item.requiredPermissions) return true;
      return item.requiredPermissions.some((p) => user?.permissions?.includes(p));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.permissions, user?.role, conversationCount]);

  const isActive = (path: string) =>
    path === '/dashboard'
      ? location.pathname === '/dashboard'
      : location.pathname.startsWith(path);

  const userName    = [user?.first_name, user?.last_name].filter(Boolean).join(' ') || 'User';
  const userInitial = user?.first_name?.charAt(0)?.toUpperCase() ?? 'U';
  const userEmail   = (user as any)?.email ?? '';
  const avatarUrl   = (user as any)?.avatar_url || undefined;
  const pageTitle   = getPageTitle(location.pathname);

  // ── Render ──────────────────────────────────────────────────────────────────
  // Key forces SidebarProvider to fully remount when layout config changes so
  // its internal state (dimensions, data-* attributes, peer-* CSS selectors)
  // re-initialises cleanly — prevents overlap/misalignment on variant/side swap.
  const layoutKey = `${layoutConfig.sidebarVariant}-${layoutConfig.sidebarCollapsible}-${layoutConfig.sidebarSide}`;

  return (
    <CallingProvider isAgent agentId={String(user?.id ?? '')}>
    <SidebarProvider key={layoutKey} className="h-svh overflow-hidden bg-background">

      {/* ── Left sidebar ────────────────────────────────────────────────── */}
      <AppSidebar
        menuItems={menuItems}
        isActive={isActive}
        onNavigate={navigate}
        userName={userName}
        userInitial={userInitial}
        userEmail={userEmail}
        avatarUrl={avatarUrl}
        sidebarVariant={layoutConfig.sidebarVariant}
        sidebarCollapsible={layoutConfig.sidebarCollapsible}
        sidebarSide={layoutConfig.sidebarSide}
      />

      {/* ── Main area (header + content) ────────────────────────────────── */}
      {/* Fixed-height flex column: page scroll lives in <main>, so full-bleed */}
      {/* pages (AI Studio) can fill remaining height without crushing the tabs. */}
      <SidebarInset className={cn(
        '!min-h-0 flex-1 overflow-hidden',
        // When sidebar is on the right: visually swap order (peer-* CSS selectors
        // use DOM order so AppSidebar must stay first in DOM) and flip inset margins
        layoutConfig.sidebarSide === 'right' && 'order-first md:peer-data-[variant=inset]:ml-2 md:peer-data-[variant=inset]:mr-0',
      )}>

        {/* Topbar */}
        <DashboardTopbar
          userName={userName}
          userInitial={userInitial}
          avatarUrl={avatarUrl}
          pageTitle={pageTitle}
          onSearch={openCommandBar}
          onNavigate={navigate}
          onLogout={handleLogout}
        />

        {/* Page content — scroll here (not on SidebarInset) so height is bounded */}
        <main
          className={cn(
            'flex min-h-0 flex-1 flex-col',
            location.pathname.startsWith('/dashboard/ai-studio')
              ? 'overflow-hidden'
              : 'overflow-y-auto',
          )}
        >
          {children}
        </main>

      </SidebarInset>

      {/* Floating overlays */}
      <CommandBar open={isCommandBarOpen} onClose={closeCommandBar} />
      <ShortcutsHelpModal open={isHelpModalOpen} onClose={closeHelpModal} />
      <Toaster />
      <CustomizerPanel layoutConfig={layoutConfig} onLayoutChange={setLayoutConfig} />
      <GlobalCallingWidgets />
      {/* 2026-09-09: Digo bot disabled per request — commented out, not removed. */}
      {/* <DigoLauncher /> */}

    </SidebarProvider>
    </CallingProvider>
  );
};

export default DashboardLayout;
